import { Form, FormInstance } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { COMPANY_URLS } from '@/constants/urls';
import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import { PaymentMethod, PaymentMethodTypeLabels } from '@/model/General/PaymentMethod';
import { Technician } from '@/model/General/Technician';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

/**
 * Hook que gestiona la selección y creación de métodos de pago
 * del técnico y del cliente para el formulario de orden de servicio.
 */
const usePaymentMethods = (
  form: FormInstance | undefined,
  selectedTechnician: Technician | null,
  selectedCompany: number | undefined,
  disabled?: boolean,
) => {
  const { t } = useTranslation();
  const { companyRepository } = useRepositoriesContext();
  const technicianPay = Form.useWatch('technician_pay', form);
  const customerInvoice = Form.useWatch('customer_invoice', form);
  const {
    selectProps: technicianPaymentMethodsSelectProps,
    refetchData: refetchTechnicianPaymentMethods,
  } = useSelectScrollSearch(
    {
      resource: COMPANY_URLS.PAYMENT_METHOD_RESOURCE,
      optionLabel: (item: any) =>
        `${t(PaymentMethodTypeLabels[item.tipo])} - ${item.descripcion ?? ''}`,
      optionValue: (item: any) => item.id,
      queryOptions: {
        enabled: !!selectedTechnician?.empresa && !!technicianPay && !disabled,
      },
      filters: [
        {
          value: selectedTechnician?.empresa,
          operator: 'eq' as const,
          field: 'empresa',
        },
      ],
    },
    {
      saveItem: true,
    },
  );
  // Métodos de pago del cliente, filtrados por la empresa seleccionada
  const { selectProps: clientPaymentMethodsSelectProps, refetchData: refetchClientPaymentMethods } =
    useSelectScrollSearch(
      {
        resource: COMPANY_URLS.PAYMENT_METHOD_RESOURCE,
        optionLabel: (item: any) =>
          `${t(PaymentMethodTypeLabels[item.tipo])} - ${item.descripcion ?? ''}`,
        optionValue: (item: any) => item.id,
        queryOptions: {
          enabled: !!selectedCompany && !!customerInvoice && !disabled,
        },
        filters: [
          {
            value: selectedCompany,
            operator: 'eq' as const,
            field: 'empresa',
          },
        ],
      },
      {
        saveItem: true,
      },
    );
  const createPaymentMethod = useCallback(
    (paymentMethodData: Partial<PaymentMethod>) => {
      if (!selectedTechnician?.empresa) return;
      void companyRepository
        .createPaymentMethod({ ...paymentMethodData, empresa: selectedTechnician.empresa })
        .then(() => refetchTechnicianPaymentMethods());
    },
    [companyRepository, refetchTechnicianPaymentMethods, selectedTechnician],
  );
  const createClientPaymentMethod = useCallback(
    (paymentMethodData: Partial<PaymentMethod>) => {
      if (!selectedCompany) return;
      void companyRepository
        .createPaymentMethod({ ...paymentMethodData, empresa: selectedCompany })
        .then(() => refetchClientPaymentMethods());
    },
    [companyRepository, refetchClientPaymentMethods, selectedCompany],
  );

  return {
    technicianPaymentMethodsSelectProps,
    createPaymentMethod,
    clientPaymentMethodsSelectProps,
    createClientPaymentMethod,
  };
};

export default usePaymentMethods;
